import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dimensions, StatusBar, View } from 'react-native';
import MapView from 'react-native-maps';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import { InfoWindow } from '@react-google-maps/api';
import { useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';

import EmptyData from '../../Components/EmptyData';
import HorizontalListing, {
  getMarginStyle,
} from '../../Components/HorizontalListing';
import List from '../../Components/List';
import Map, { Marker } from '../../Components/Map';
import constants from '../../Config/constants';
import { numberToArray } from '../../Helper';
import DateHelper from '../../Helper/Date';
import routeList from '../../Routes/list';
import { EventDetails } from '../../RTK/api/events/types';
import { useGetListingQuery } from '../../RTK/api/reservations';
import { activeAddressSelector } from '../../RTK/user/selectors';
import ReservationCard from '../../Screens/Reservation/components/Card';
import ThemeStyle from '../../Theme/styles';
import breakpoints from '../../Theme/styles/breakpoints';

import Header from './Header';
import ReservationCardSmall from './ReservationCardSmall';
import ReservationCardXSmall from './ReservationCardXSmall';
import { ids, styles } from './styles';

type EventDetailsWithloading = EventDetails & {
  loading?: boolean;
};
type LatLngMobile = {
  latitude: number;
  longitude: number;
};
type LatLngWeb = {
  lat: number;
  lng: number;
};
type LatLng =
  | (LatLngMobile & { lat?: undefined; lng?: undefined })
  | (LatLngWeb & { latitude?: undefined; longitude?: undefined });
type GetLatLng = (
  latitude: string | number,
  longitude: string | number,
) => LatLng;
const delta =
  0.044 * // the zoom level 13
  (constants.DEVICE_WIDTH / constants.DEVICE_HEIGHT);
const getLatLng: GetLatLng = (latitude, longitude) =>
  constants.isWeb
    ? { lng: Number(longitude), lat: Number(latitude) }
    : {
        longitude: Number(longitude),
        latitude: Number(latitude),
        latitudeDelta: delta, // or a value that fits your zoom level
        longitudeDelta: delta,
      };

const ReservationLocateOnMap = ({ navigation, onClose }) => {
  const mapRef = useRef<MapView>();
  const listRef = useRef<{ listRef: List }>();
  const fitToBoundsOnce = useRef<boolean>(false);
  const theme = useTheme();
  const activeAddress = useSelector(activeAddressSelector);
  const [longitude, latitude] = activeAddress.geo_json_point.coordinates; // default used address
  const [webMap, setWebMap] = useState('');
  const [selectedMarker, setSelectedMarker] = useState<
    { location: LatLngWeb; data: EventDetails } | undefined
  >();
  const [filter, setFilter] = useState({
    party_size: 1,
    location: `${latitude},${longitude}`,
    date: dayjs().format(constants.DBDATE_FORMAT),
    time: dayjs(
      DateHelper.getTimeSlots()[0],
      constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
    ).format(constants.DBTIME_FORMAT),
  });

  const { data, isFetching } = useGetListingQuery({
    payload: {
      limit: 10,
      offset: 0,
      ...filter,
      type: 'all',
    },
  });

  const result = isFetching
    ? numberToArray(9).map(() => ({ loading: true }))
    : data?.result || [];

  const locations = isFetching
    ? []
    : result.map((e) => {
        const { lng, lat } = e.store.geocode_location;
        return {
          id: e.store.id,
          location: getLatLng(lat, lng),
        };
      });

  // @ts-ignore
  const latLng = getLatLng(...filter.location.split(',')); // this spread the arrat ["lat", "lng"] into 2 args to pass on getLatLng
  const getPinIcon = useMemo(() => {
    if (constants.isWeb) {
      return {
        url: 'https://pickup-marketplace.s3.ap-southeast-1.amazonaws.com/assets/location-pin.svg',
        scaledSize: {
          width: 40,
          height: 40,
        },
      };
    } else {
      return {
        name: 'pin',
        fill: theme['color-info-500'],
        stroke: theme['color-info-500'],
      };
    }
  }, []);

  // Hooks for moving the camera to bounding box of all coordinates
  if (constants.isWeb) {
    useEffect(() => {
      if (!isFetching && webMap && !fitToBoundsOnce.current) {
        fitToBoundsOnce.current = true;
        setTimeout(() => {
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(latLng as LatLngWeb);
          locations.map((marker) => {
            bounds.extend(marker.location as LatLngWeb);
          });
          // @ts-ignore
          webMap.fitBounds(bounds);
        }, 250);
      }
    }, [webMap, isFetching]);
  } else {
    useEffect(() => {
      if (!isFetching && !fitToBoundsOnce.current) {
        fitToBoundsOnce.current = true;
        setTimeout(() => {
          mapRef.current.fitToCoordinates(
            [
              latLng, // activeAddress
              ...locations.map((e) => e.location), // results locations
            ] as LatLngMobile[],
            {
              animated: true,
              edgePadding: {
                top: 200,
                right: 20,
                bottom: constants.isIOS ? 350 : 300,
                left: 20,
              },
            },
          );
        }, 250);
      }
    }, [isFetching]);
  }

  const _navigateToStore = (storeId) => {
    // TODO - fix url parameter when navigating to store details from map
    _onClose();
    navigation.navigate(routeList.RES_STORE_DETAILS, { storeId });
  };

  const _onClose = () => {
    if (constants.isWeb) {
      onClose();
    } else {
      navigation.goBack();
    }
  };

  const _onFilterChange = (data) => {
    setTimeout(() => {
      setFilter((prev) => ({ ...prev, ...data }));
    }, 0);
  };

  const _renderEmpty = () => {
    return (
      <View style={[ThemeStyle.flex1, ThemeStyle.pageVerticalSpacing]}>
        <EmptyData
          type="filter"
          fill={theme['color-info-500']}
          subtitle="No available events or reservations within this area"
        />
      </View>
    );
  };

  const _onLocationPress = (loc: { id: string; location: LatLng }) => {
    const [_, size] = breakpoints.md.split(' ');
    const mediumSize = Number(size.replace('px)', ''));
    const listItemIndex = lodashFindIndex(
      result as EventDetailsWithloading[],
      (e) => e.store.id === loc.id,
    );
    if (constants.isWeb && Dimensions.get('window').width >= mediumSize) {
      setSelectedMarker({
        data: result[listItemIndex] as EventDetailsWithloading,
        location: loc.location as LatLngWeb,
      });
    } else {
      if (listItemIndex !== -1) {
        listRef.current.listRef._scrollToIndex({
          animated: true,
          index: listItemIndex,
          viewPosition: 0.5,
        });
      }
    }
  };

  return (
    <View
      // @ts-ignore: some style works on web, but treated as warning on react native
      style={styles.wrapper}
    >
      <StatusBar
        backgroundColor="transparent"
        barStyle="dark-content"
        translucent
      />
      {/* Web result, still has responsive style */}
      <View
        dataSet={{ media: ids.firstColumn }}
        // @ts-ignore: some style works on web, but treated as warning on react native
        style={styles.firstColumn}
      >
        <View
          dataSet={{ media: ids.gridContainer }}
          style={result.length === 0 ? ThemeStyle.flex1 : styles.gridContainer}
        >
          {result.length === 0
            ? _renderEmpty()
            : result.map((data, i) => (
                <ReservationCard
                  key={i}
                  {...data}
                  params={filter}
                  onNavigate={() => onClose?.()}
                />
              ))}
        </View>
      </View>
      <View
        dataSet={{ media: ids.secondColumn }}
        // @ts-ignore: some style works on web, but treated as warning on react native
        style={styles.secondColumn}
      >
        {/* Header on map */}
        <Header
          onClose={_onClose}
          onFilterChanged={_onFilterChange}
          filter={filter}
        />
        {/* Actual map */}
        <Map
          onRef={(r) => (mapRef.current = r)}
          {...(!constants.isWeb && {
            region: latLng,
            initialCamera: {
              center: latLng,
              zoom: 13,
              pitch: 0, // add this
              heading: 0, // add this
            },
          })}
          zoom={14}
          onRegionChanged={debounce((newCenter) => {
            _onFilterChange({
              location: `${newCenter.latitude},${newCenter.longitude}`,
            });
          }, 500)}
          onLoadCustom={setWebMap}
          region={latLng}
          withPosition={false} // web props
        >
          {/* Result locations */}
          {locations.map((d, i) => (
            <Marker
              key={d.id}
              coordinate={d.location}
              draggable={false} // web props
              icon={getPinIcon}
              onPress={() => _onLocationPress(d)}
            />
          ))}
          {/* Web popup for selected address */}
          {constants.isWeb && !lodashIsEmpty(selectedMarker) && (
            <InfoWindow
              position={selectedMarker.location}
              options={{
                headerDisabled: true,
                pixelOffset: new window.google.maps.Size(0, -45),
              }}
              onCloseClick={() => setSelectedMarker(undefined)}
            >
              <ReservationCardXSmall
                {...selectedMarker.data}
                onClose={() => setSelectedMarker(undefined)}
                onPress={() => _navigateToStore(selectedMarker.data.store.id)}
              />
            </InfoWindow>
          )}
        </Map>
        {/* Mobile Result */}
        <View
          dataSet={{ media: ids.itemWrapperMobile }}
          // @ts-ignore: some style works on web, but treated as warning on react native
          style={styles.itemWrapperMobile}
        >
          <SafeAreaView edges={['bottom']}>
            <HorizontalListing
              ref={listRef}
              // @ts-ignore: this file is using the data but the ts says undefined, this file is js file
              data={result}
              renderItem={({ item, isFirstOfList, isLastOfList }) => (
                <View
                  style={getMarginStyle(isFirstOfList, isLastOfList, false)}
                >
                  <ReservationCardSmall
                    {...item}
                    onPress={() => _navigateToStore(item.store.id)}
                  />
                </View>
              )}
              keyExtractor={(item, index) =>
                item?.store?.id || index.toString()
              }
              horizontal
              plain
            />
          </SafeAreaView>
        </View>
      </View>
    </View>
  );
};

export default ReservationLocateOnMap;
